import {
  X,
  Phone,
  Mail,
  MapPin,
  Package,
  ShieldCheck,
  IndianRupee,
  Clock,
  Database,
} from "lucide-react";
import { formatDateTime } from "../../Component/Common/formatDateTime";

export function ViewLeadModal({ lead, onClose }) {

  const LEAD_TYPE_MAP = {
    2: "Company",
    4: "Agent",
    5: "Retailer",
  };
  if (!lead) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-3">
      <div className="bg-white w-full max-w-5xl h-[90vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden">

        {/* ================= HEADER ================= */}
        <div className="flex justify-between items-center px-6 py-4 border-b bg-slate-50 shrink-0">
          <div>
            <div className="flex gap-2">
              <h2 className="text-lg font-semibold text-slate-900">
              Lead Details
            </h2>

            <span
                className={`px-4 py-2 mb-2 rounded-full text-xs font-semibold capitalize ${
                  lead.status === "won"
                    ? "bg-green-100 text-green-700"
                    : lead.status === "lost"
                    ? "bg-red-100 text-red-700"
                    : lead.status === "in progress"
                    ? "bg-amber-100 text-amber-700"
                    : "bg-blue-100 text-blue-700"
                }`}
              >
                {lead.status}
              </span>
            </div>
            <p className="text-xs text-slate-500">
              Lead Code:{" "}
              <span className="font-semibold">{lead.lead_code}</span>
            </p>
          </div>

          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-slate-200 transition"
          >
            <X className="w-5 h-5 text-slate-700" />
          </button>
        </div>

        {/* ================= BODY (SCROLLABLE) ================= */}
        <div className="grid grid-cols-1 sm:grid-cols-2 overflow-y-auto gap-3 px-6 py-6 bg-slate-50 ">

          {/* BUSINESS */}
          <Card title="Business & Contact" icon={<ShieldCheck />}>
            <Info label="Business Name" value={lead.name} />
            <Info label="Owner Name" value={lead.owner_name} />
            <Info icon={<Phone size={14} />} label="Phone" value={lead.phone} />
            <Info icon={<Mail size={14} />} label="Email" value={lead.email} />
          </Card>

          {/* LOCATION */}
          <Card title="Location" icon={<MapPin />}>
            <Info label="State" value={lead.state} />
            <Info label="District" value={lead.district} />
            <Info label="Pincode" value={lead.pincode} />
            <Info label="State Code" value={lead.state_in} />
            <Info label="District Code" value={lead.district_in} />
            <Info label="Address" value={lead.address_full} full />
          </Card>

          {/* PACKAGE & FINANCIAL */}
          <Card title="Package & Financials" icon={<Package />}>
            <Info label="Package Name" value={lead.package_name} />
            {/* <Info label="Package ID" value={lead.package_id} /> */}
            <Info label="Badge Name" value={lead.badge_name} />
            {/* <Info label="Badge ID" value={lead.badge_id} /> */}
            <Info label="Benefits" value={lead.benefits} />
            <Info label="Eligibility" value={lead.eligibility} />

            <Info
              icon={<IndianRupee size={14} />}
              label="Lead Amount"
              value={lead.lead_amount}
            />

            {/* PAYMENT METHOD */}
            <div className="">
              <p className="text-xs text-slate-500 mb-1">Payment Method</p>

              {lead.pay_now === 1 || lead.pay_later === 1 ? (
                <div className="flex gap-2 flex-wrap">
                  {lead.pay_now === 1 && (
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">
                      Pay Now
                    </span>
                  )}

                  {lead.pay_later === 1 && (
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">
                      Pay Later
                    </span>
                  )}
                </div>
              ) : (
                <span className="text-xs font-medium text-slate-400 italic">
                  Payment method not added
                </span>
              )}
            </div>
          </Card>


          {/* STATUS */}
          {lead.status !== "won" && (
            <Card title="Lead Status">
            <div className="flex items-center gap-3 md:col-span-2">
              <span className="text-sm font-medium text-slate-600">
                {lead.status}
              </span>
              
            </div>

            {lead.remark && (
              <div className="md:col-span-2">
                <p className="text-xs font-semibold text-slate-500 mb-1">
                  Remark
                </p>
                <p className="text-sm text-slate-700 bg-white border rounded-xl p-3">
                  {lead.remark}
                </p>
              </div>
            )}
          </Card>
          )}

          {/* SYSTEM / AUDIT */}
          <Card title="System & Audit Info" icon={<Database />}>
            <Info label="Lead ID" value={lead.id} />
            <Info
              label="Lead Type"
              value={LEAD_TYPE_MAP[lead.lead_type] || "-"}
            />
            <Info label="Company Code" value={lead.company_code} />
            <Info label="Agent ID" value={lead.agent_id} />
            <Info label="Manager ID" value={lead.manager_id} />
            <Info label="Created By" value={lead.created_by_name} />
            <Info label="Updated By" value={lead.updated_by_name} />

            <Info
              icon={<Clock size={14} />}
              label="Created At"
              value={formatDateTime(lead.created_at)}
            />
            <Info
              icon={<Clock size={14} />}
              label="Updated At"
              value={formatDateTime(lead.updated_at)}
            />
          </Card>
        </div>

        {/* ================= FOOTER ================= */}
        <div className="px-6 py-4 border-t bg-white shrink-0 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

/* ================= REUSABLE COMPONENTS ================= */

function Card({ title, icon, children }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
      <div className="flex items-center gap-2 mb-4">
        {icon && <span className="text-blue-600">{icon}</span>}
        <h3 className="text-sm font-semibold text-slate-800">
          {title}
        </h3>
      </div>

      {/* STRICT 2-COLUMN GRID */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
        {children}
      </div>
    </div>
  );
}

function Info({ label, value, full, icon }) {
  if (value === null || value === undefined || value === "") return null;

  return (
    <div className={full ? "md:col-span-2" : ""}>
      <p className="text-xs text-slate-500 flex items-center gap-1">
        {icon && <span className="text-slate-400">{icon}</span>}
        {label}
      </p>
      <p className="text-sm font-medium text-slate-800 break-words">
        {String(value)}
      </p>
    </div>
  );
}

